import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, Package, ArrowUpCircle, ArrowDownCircle } from "lucide-react";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function MovimentacoesPage() {
  const [produtos, setProdutos] = useState([]);
  const [carros, setCarros] = useState([]);
  const [movimentacoes, setMovimentacoes] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tipoMovimentacao, setTipoMovimentacao] = useState("entrada"); // entrada ou transferencia
  
  const [formData, setFormData] = useState({
    produto_id: "",
    quantidade: "",
    carro_id_destino: "",
    motivo: ""
  });

  useEffect(() => {
    loadProdutos();
    loadCarros();
    loadMovimentacoes();
  }, []);

  const loadProdutos = async () => {
    try {
      const response = await axios.get(`${API}/produtos`);
      setProdutos(response.data);
    } catch (error) {
      toast.error("Erro ao carregar produtos");
    }
  };

  const loadMovimentacoes = async () => {
    try {
      const response = await axios.get(`${API}/movimentacoes`);
      setMovimentacoes(response.data);
    } catch (error) {
      toast.error("Erro ao carregar movimentações");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const data = {
        produto_id: formData.produto_id,
        tipo: tipoMovimentacao === "entrada" ? "entrada_loja" : "transferencia",
        quantidade: parseInt(formData.quantidade),
        motivo: formData.motivo || undefined
      };

      // Se for transferência, adicionar carro destino
      if (tipoMovimentacao === "transferencia") {
        data.carro_id_destino = formData.carro_id_destino;
      }

      const response = await axios.post(`${API}/movimentacoes`, data);
      
      toast.success(response.data.message);
      setDialogOpen(false);
      resetForm();
      loadProdutos();
      loadMovimentacoes();
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao registrar movimentação";
      toast.error(message);
    }
  };

  const resetForm = () => {
    setFormData({
      produto_id: "",
      quantidade: "",
      carro_id_destino: "",
      motivo: ""
    });
    setTipoMovimentacao("entrada");
  };

  const loadCarros = async () => {
    try {
      const response = await axios.get(`${API}/carros`);
      setCarros(response.data);
    } catch (error) {
      console.error("Erro ao carregar carros:", error);
    }
  };

  const handleDialogClose = (open) => {
    setDialogOpen(open);
    if (!open) {
      resetForm();
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR');
  };

  const getMovimentacaoIcon = (tipo) => {
    if (tipo === "entrada_loja") {
      return <ArrowUpCircle className="h-5 w-5 text-green-600" />;
    } else if (tipo === "transferencia") {
      return <ArrowUpCircle className="h-5 w-5 text-blue-600" />;
    } else if (tipo === "venda_carro") {
      return <ArrowDownCircle className="h-5 w-5 text-red-600" />;
    } else if (tipo === "devolucao_carro") {
      return <ArrowUpCircle className="h-5 w-5 text-orange-600" />;
    }
    return <ArrowDownCircle className="h-5 w-5 text-gray-600" />;
  };

  const getMovimentacaoBadge = (tipo) => {
    if (tipo === "entrada_loja") {
      return (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Entrada na Loja
        </span>
      );
    } else if (tipo === "transferencia") {
      return (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          Transferência
        </span>
      );
    } else if (tipo === "venda_carro") {
      return (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          Venda do Carro
        </span>
      );
    } else if (tipo === "devolucao_carro") {
      return (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
          Devolução
        </span>
      );
    }
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        {tipo}
      </span>
    );
  };

  const getProdutoNome = (produtoId) => {
    const produto = produtos.find(p => p.id === produtoId);
    return produto ? produto.nome : "Produto não encontrado";
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold" style={{fontFamily: 'Work Sans'}}>Movimentações de Estoque</h2>
        <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
          <DialogTrigger asChild>
            <Button className="gap-2 bg-blue-500 hover:bg-blue-600">
              <Package className="h-4 w-4" />
              Nova Movimentação
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Registrar Movimentação</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Tipo de Movimentação */}
              <div className="space-y-2">
                <Label>Tipo de Movimentação *</Label>
                <div className="grid grid-cols-2 gap-4">
                  <Button
                    type="button"
                    variant={tipoMovimentacao === "entrada" ? "default" : "outline"}
                    className={tipoMovimentacao === "entrada" ? "bg-green-600 hover:bg-green-700" : ""}
                    onClick={() => setTipoMovimentacao("entrada")}
                  >
                    <TrendingUp className="h-4 w-4 mr-2" />
                    Entrada na Loja
                  </Button>
                  <Button
                    type="button"
                    variant={tipoMovimentacao === "transferencia" ? "default" : "outline"}
                    className={tipoMovimentacao === "transferencia" ? "bg-blue-600 hover:bg-blue-700" : ""}
                    onClick={() => setTipoMovimentacao("transferencia")}
                  >
                    <TrendingDown className="h-4 w-4 mr-2" />
                    Transferência para Carro
                  </Button>
                </div>
              </div>

              {/* Produto */}
              <div className="space-y-2">
                <Label htmlFor="produto_id">Produto *</Label>
                <select
                  id="produto_id"
                  required
                  value={formData.produto_id}
                  onChange={(e) => setFormData({...formData, produto_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">Selecione um produto</option>
                  {produtos.map((produto) => (
                    <option key={produto.id} value={produto.id}>
                      {produto.nome} ({produto.codigo_sku}) - Estoque: {produto.estoque_atual}
                    </option>
                  ))}
                </select>
              </div>

              {/* Quantidade */}
              <div className="space-y-2">
                <Label htmlFor="quantidade">Quantidade *</Label>
                <Input
                  id="quantidade"
                  type="number"
                  min="1"
                  required
                  value={formData.quantidade}
                  onChange={(e) => setFormData({...formData, quantidade: e.target.value})}
                  placeholder="Digite a quantidade"
                />
              </div>

              {/* Carro de Destino (obrigatório para transferência) */}
              {tipoMovimentacao === "transferencia" && (
                <div className="space-y-2">
                  <Label htmlFor="carro_destino">Carro de Destino *</Label>
                  <select
                    id="carro_destino"
                    required
                    value={formData.carro_id_destino}
                    onChange={(e) => setFormData({...formData, carro_id_destino: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Selecione o carro</option>
                    {carros.map((carro) => (
                      <option key={carro.id} value={carro.id}>
                        {carro.nome}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {tipoMovimentacao === "entrada" && (
                <div className="space-y-2">
                  <Label htmlFor="motivo">Motivo da Entrada (opcional)</Label>
                  <Input
                    id="motivo"
                    value={formData.motivo}
                    onChange={(e) => setFormData({...formData, motivo: e.target.value})}
                    placeholder="Ex: Reposição, Compra de fornecedor"
                  />
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => handleDialogClose(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className={tipoMovimentacao === "entrada" ? "bg-green-600 hover:bg-green-700" : "bg-blue-600 hover:bg-blue-700"}>
                  Registrar {tipoMovimentacao === "entrada" ? "Entrada" : "Transferência"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Histórico de Movimentações */}
      <Card>
        <CardHeader>
          <CardTitle>Histórico de Movimentações</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {movimentacoes.map((mov) => (
              <div
                key={mov.id}
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
              >
                <div className="flex items-center gap-4 flex-1">
                  <div>{getMovimentacaoIcon(mov.tipo)}</div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{mov.produto_nome}</p>
                      {getMovimentacaoBadge(mov.tipo)}
                    </div>
                    <p className="text-sm text-gray-500">SKU: {mov.produto_codigo}</p>
                    {mov.carro_nome && (
                      <p className="text-xs text-blue-600 mt-1">
                        Carro: {mov.carro_nome}
                      </p>
                    )}
                    {mov.motivo && (
                      <p className="text-xs text-gray-500 mt-1">Motivo: {mov.motivo}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">{formatDate(mov.created_at)}</p>
                  </div>
                  
                  <div className="text-right">
                    <p className={`font-bold text-lg ${mov.tipo === "entrada_loja" ? "text-green-600" : "text-red-600"}`}>
                      {mov.tipo === "entrada_loja" ? "+" : "-"}{mov.quantidade}
                    </p>
                    <p className="text-xs text-gray-500">
                      {mov.estoque_anterior_origem} → {mov.estoque_posterior_origem}
                    </p>
                  </div>
                </div>
              </div>
            ))}

            {movimentacoes.length === 0 && (
              <div className="text-center py-12">
                <Package className="h-12 w-12 mx-auto text-gray-400" />
                <p className="text-gray-500 mt-4">Nenhuma movimentação registrada</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
